import { useLayoutEffect, useRef } from "react";
import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import ListItem from "@/components/ListItem";
import SectionHeading from "@/components/SectionHeading";
import type { CareerPlanAssessmentAnswer } from "./careerPlanHistory";
import { focusedExperienceIndexAnswerGroups } from "./experienceIndexAnswers";

export default function ExperienceIndexAnswersTab({
  answers,
  focusedCompetencyId,
  onClearFocusedCompetency
}: {
  answers: CareerPlanAssessmentAnswer[];
  focusedCompetencyId?: string | null;
  onClearFocusedCompetency?: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const emptyFocusRef = useRef<HTMLDivElement>(null);
  const wasFocusedRef = useRef(false);
  const groups = focusedExperienceIndexAnswerGroups(answers, focusedCompetencyId);
  const focusedCompetencyName = groups[0]?.[0]?.competencyName;
  const isFocused = Boolean(focusedCompetencyId);
  const isEmptyFocus = isFocused && groups.length === 0;

  useLayoutEffect(() => {
    if (isEmptyFocus) {
      emptyFocusRef.current?.focus();
      wasFocusedRef.current = true;
      return;
    }

    if (isFocused || wasFocusedRef.current) {
      headingRef.current?.focus();
    }

    wasFocusedRef.current = isFocused;
  }, [focusedCompetencyId, isEmptyFocus, isFocused]);

  if (answers.length === 0) {
    return (
      <EmptyState
        title="No Experience Index Answers"
        iconName="quiz"
        caption="This person has not answered Experience Index questions yet."
      />
    );
  }

  if (isEmptyFocus) {
    return (
      <div
        ref={emptyFocusRef}
        tabIndex={-1}
        role="region"
        aria-label="No Answers for This Competency"
        className="flex flex-col gap-6"
        data-testid="career-plan-ei-answers-tab"
      >
        <EmptyState
          title="No Answers for This Competency"
          iconName="quiz"
          caption="This person has not answered questions for this competency yet."
        />
        {onClearFocusedCompetency ? (
          <Button variant="outlined" onClick={onClearFocusedCompetency} testId="career-plan-ei-answers-view-all">
            View all answers
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="career-plan-ei-answers-tab"
      data-focused={isFocused ? "true" : undefined}
    >
      <SectionHeading
        headingRef={headingRef}
        heading={isFocused && focusedCompetencyName ? focusedCompetencyName : "Experience Index Answers"}
        body={
          isFocused
            ? "Read-only question-level answers for this competency."
            : "Current question-level answers. Historical snapshots do not include answers."
        }
      />
      {isFocused && onClearFocusedCompetency ? (
        <Button variant="outlined" onClick={onClearFocusedCompetency} testId="career-plan-ei-answers-view-all">
          View all answers
        </Button>
      ) : null}
      {groups.map((group) => (
        <div
          key={group[0].competencyId}
          className="flex flex-col gap-2"
          data-testid={`career-plan-ei-answers-${group[0].competencyId}`}
        >
          {isFocused ? null : (
            <p className="text-title-medium-bold font-serif text-sys-brand-on-surface">{group[0].competencyName}</p>
          )}
          {group.map((answer) => (
            <ListItem
              key={`${answer.questionId}-${answer.optionId}`}
              iconName="question_mark"
              overline={answer.questionContent}
              headline={answer.optionContent}
              longContent
              iconBackground
            />
          ))}
        </div>
      ))}
    </div>
  );
}
